import type { VatDetailsCellInput } from '~/components/Finances/vatDetailsCell';
import { getNetAmount } from '~/components/Finances/vatDetailsCell';

interface FinanceRowVatDetailsInput {
  amount?: number | null;
  amount_orig_currency?: number | null;
  request_currency?: string | null;
  policy_currency?: string | null;
  payment_request?: {
    is_vat_included?: boolean | null;
    vat_amount?: number | null;
  } | null;
}

// vat_amount is entered in the policy (base) currency - the same basis as the Base Amount column
// and the Recoveries tab. Render it, and Net (amount - VAT), against the policy-currency amount
// (row.amount) so both sides share one currency. Pairing the policy-currency VAT with the
// request-currency amount produced a cross-currency Net (a £100 request with a $132 VAT rendered
// "£132.00 / Net: -£32.00").
export const getFinanceRowVatDetails = (
  row: FinanceRowVatDetailsInput,
  { showGrossInsteadOfNet }: { showGrossInsteadOfNet?: boolean } = {}
): VatDetailsCellInput => ({
  isVatIncluded: row.payment_request?.is_vat_included,
  vatAmount: row.payment_request?.vat_amount,
  currency: row.policy_currency,
  amount: row.amount,
  showGrossInsteadOfNet,
});

export interface FinanceRowAmountDisplay {
  // Figure to render in the Amount column.
  amount?: number | null;
  // Currency to format `amount` in, or null when the row carries no original request currency and the
  // cell must keep using the table's ambient currency formatter.
  currency: string | null;
}

const isSameCurrency = (left?: string | null, right?: string | null): boolean =>
  Boolean(left && right && left.trim().toUpperCase() === right.trim().toUpperCase());

// Which figure the Amount column shows, and in which currency. The column has two currency bases but
// the row carries a single VAT figure, `payment_request.vat_amount`, entered in the policy (base)
// currency - the same basis as `row.amount`. So:
//   * `row.amount` (policy basis) is always safe to net against it;
//   * `row.amount_orig_currency` (request basis) is only safe to net when the request currency IS the
//     policy currency, i.e. when the two bases coincide. finance_model exposes no request-currency VAT
//     field (only the nested payment_request's `vat_amount`), and converting the policy-currency VAT
//     here would re-introduce the cross-currency bug described above, so a genuinely cross-currency row
//     keeps showing its gross request-currency amount.
// A row with no usable VAT figure (VAT not included, or vat_amount missing) also keeps its gross amount:
// there is nothing to subtract, so net equals gross.
export const getFinanceRowAmountDisplay = (
  row: FinanceRowVatDetailsInput,
  { displayNetAmounts }: { displayNetAmounts?: boolean } = {}
): FinanceRowAmountDisplay => {
  const usesRequestCurrency = Boolean(row.request_currency && row.amount_orig_currency);
  const amount = usesRequestCurrency ? row.amount_orig_currency : row.amount;
  const currency = usesRequestCurrency ? (row.request_currency ?? null) : null;

  if (!displayNetAmounts || !row.payment_request?.is_vat_included) {
    return { amount, currency };
  }

  const isVatOnSameBasis = !usesRequestCurrency || isSameCurrency(row.request_currency, row.policy_currency);
  const netAmount = isVatOnSameBasis ? getNetAmount(amount, row.payment_request?.vat_amount) : null;

  return { amount: netAmount ?? amount, currency };
};
